import {
  currency
} from '@/utils';
import {
  visaOptions
} from '@/config';

// TODO: should some of these functions be placed into components instead?

export default {
  /** get the current step name based on the current step index */
  currentStepName: state => state.steps[state.stepIndex],

  /** get entire quote subtotal based on rooms, excursions, visas and donations */
  runningSubtotal: (state, getters) => {
    let subtotal = state.rooms.reduce((accumulator, room) => {
      if (room.id) {
        return accumulator + (room.pricePerPerson * room.occupancy);
      }
      return accumulator;
    }, 0);

    subtotal = state.excursions.reduce((accumulator, excursion) => {
      return accumulator + (excursion.price * excursion.passengers.filter(passenger => passenger.selected).length);
    }, subtotal);

    subtotal = getters.selectedVisas.reduce((accumulator, visa) => {
      return accumulator + (visa.price * visa.count);
    }, subtotal);

    var donatedAmount = 0;

    if (getters.donationAllocationType == 'PerPerson') {
      const allTravellers = state.rooms.flatMap(r => r.passengers);
      donatedAmount = (allTravellers.length - state.nonDonatingTravellers.length) * getters.donationData.price;
    }

    if (getters.donationAllocationType == 'PerBooking') {
      donatedAmount = !state.donationPerBooking ? getters.donationData.price : 0;
    }

    return subtotal += donatedAmount;
  },

  /** does the tour have addons available */
  hasAddons: state => state.addonData && state.addonData.addOns && state.addonData.addOns.length,

  /** does the quote have an addon selected (the initial tour code will be different) */
  addonSelected: state => state.quote.initialTourCode !== state.quote.tourCode,

  /** get a room type by that room id */
  availableRoomById: state => id => state.availableRooms.find(room => room.id === id),

  /** check if we can add another room, based on the current room selection, passengers and max passengers */
  canAddRoom: (state, getters) => {
    // disallow if one room section does not have a selected room
    if (!state.rooms.every(room => room.id)) {
      return false;
    }
    return getters.roomPassengers.length < state.maxPassengers;
  },

  /** get the remaining room types for each room based on the remaining passenger allowance */
  remainingAvailableRoomsForRoom: (state, getters) => roomIndex => {
    // get other rooms
    const otherRooms = state.rooms.filter((room, index) => index !== roomIndex);

    // get total passengers in other rooms
    const otherRoomsPassengerCount = getters.roomsPassengerCount(otherRooms);

    // get remaing passenger allowance for this room
    const remainingPassengerAllowance = state.maxPassengers - otherRoomsPassengerCount;

    // return array of available rooms based on occupancy
    return state.availableRooms.filter(room => room.occupancy <= remainingPassengerAllowance);
  },

  /** build up a passenger array from each rooms passengers */
  roomPassengers: (state, getters, rootState, rootGetters) => {
    const roomPassengers = [];

    let isLead = -1;

    state.rooms.forEach(room => {
      room.passengers.forEach(passenger => {
        const passengerData = rootGetters.formDataFlat(passenger);

        // check if we have passengerDetails
        let passengerDetails = {};
        if (passenger.passengerId) {
          passengerDetails = getters.passengersDetails.find(p => p.passengerId === passenger.passengerId);
        }

        roomPassengers.push({
          ...passengerDetails,
          ...passengerData,
          roomId: room.id,
          lead: !++isLead
        });
      });
    });

    return roomPassengers;
  },

  nonDonatingTravellers: (state) => {
    return state.nonDonatingTravellers;
  },

  donatingTravellers: (state, getters, rootState, rootGetters) => {
    if (!state.donationsData.donationExists)
      return [];

    const allPassangers = state.rooms.flatMap(room => room.passengers);
    let donatingTravellers = [];

    allPassangers.forEach(passenger => {
      const passangerName = rootGetters.passengerFullName(rootGetters.formDataFlat(passenger));

      if (!state.nonDonatingTravellers.includes(passangerName)) {
        donatingTravellers.push(passangerName);
      }
    });

    return donatingTravellers;
  },

  donationExtraId: (state) => {
    return state.donationsData.id;
  },

  donationAllocationType: (state) => {
    return state.donationsData.allocationType;
  },

  donationData: (state) => {
    return state.donationsData;
  },

  donationPerBooking: (state) => {
    return state.donationPerBooking;
  },

  donated: (state) => {
    return state.donationMade;
  },

  passangersWhoDonated: (state) => {
    return state.quote.passengers.filter(p => p.donated);
  },

  // TODO: should we make this a function rather than a getter?
  /** get total count of passengers from array of rooms */
  roomsPassengerCount: () => rooms => rooms.reduce((accumulator, room) => {
    return accumulator + (room.occupancy || 0);
  }, 0),

  // TODO: should we make this a function rather than a getter?
  /** get the global passenger index taking rooms before into account */
  globalPassengerIndex: (state, getters) => (roomIndex, passengerIndex) => {
    const roomsBefore = state.rooms.slice(0, roomIndex);
    const roomsBeforePassengerCount = getters.roomsPassengerCount(roomsBefore);
    return roomsBeforePassengerCount + passengerIndex + 1;
  },

  /** get currency formatted for correct country and currency */
  currency: state => amount => currency(amount, state.quote.cultureCode, state.quote.currency),

  /** get excursions and passenger data in minimal format */
  excursions: state => {
    return state.excursions.map(excursion => ({
      code: excursion.id,
      passengerIds: excursion.passengers.filter(passenger => passenger.selected).map(passenger => passenger.passengerId)
    })).filter(excursion => excursion.passengerIds.length);
  },

  /** check if a passenger is going on an excursion */
  passengerHasExcursion: (state, getters) => (passenger, excursion) => {
    const theExcursion = getters.excursions.find(e => e.code === excursion.id);
    if (theExcursion) {
      return theExcursion.passengerIds.indexOf(passenger.passengerId) > -1;
    }
    return false;
  },

  /** get the first incomplete passenger */
  firstIncompletePassenger: (state, getters, rootState, rootGetters) => {
    return state.passengers.find(passenger => {
      return !passenger.hasBeenSubmitted || !rootGetters.formIsValid(passenger) || !passenger.summaryView;
    });
  },

  allPassengersComplete: (state, getters) => {
    return !getters.firstIncompletePassenger;
  },

  /** check if a passenger is editable */
  passengerIsEditable: (state, getters, rootState, rootGetters) => passenger => {
    // if the passenger is the next incomplete passenger
    if (getters.firstIncompletePassenger && getters.firstIncompletePassenger.passengerId === passenger.passengerId) {
      return true;
    }

    // if the passenger is complete and summary view
    if (passenger.summaryView && rootGetters.formIsValid(passenger)) {
      return true;
    }

    return false;
  },

  /** get all passenger data for sending to API */
  passengersDetails: (state, getters, rootState, rootGetters) => {
    return state.passengers.map(passenger => ({
      passengerId: passenger.passengerId,
      lead: passenger.lead,
      roomId: passenger.roomId,
      ...rootGetters.formDataFlat(passenger)
    }));
  },

  passengerHasDuplicateName: () => passengerArray => {

    // Add each full name to an array
    const getPassengers = passengerArray
      .map(passenger => `${passenger.firstName || ''} ${passenger.lastName || ''}`)
      .filter(passenger => Boolean(passenger.trim()));

    // nothing has been filled out yet
    if (!getPassengers.length) {
      return false;
    }

    // create a unique passenger set from original array
    let uniquePassengers = new Set(getPassengers);
    uniquePassengers = [...uniquePassengers];

    // compare length of unique passengers array and original passenger array
    if (getPassengers.length === uniquePassengers.length) {
      return false;
    } else {
      // if they are different then there are duplicates (and fail)
      return true;
    }

  },

  /** visa count and prices */
  selectedVisas: (state, getters) => {
    const passengerVisas = {};
    getters.passengersDetails.forEach(passenger => {
      if (passenger.visaIds) {
        for (const visaId in passenger.visaIds) {
          if (passenger.visaIds[visaId] === 'Visa required') {
            if (!passengerVisas[visaId]) {
              passengerVisas[visaId] = 0;
            }
            passengerVisas[visaId] = passengerVisas[visaId] + 1;
          }
        }
      }
    });

    return state.visas.map(visa => ({
      ...visa,
      count: passengerVisas[visa.id]
    })).filter(visa => visa.count);
  },

  /** get a visa name from the main visa array using a code */
  getVisaNameFromCode: state => visaId => {
    // loop state.visas and get the name

    for (let i = 0; i < state.visas.length; i++) {
      if (parseInt(state.visas[i].id) === parseInt(visaId)) {
        return state.visas[i].name;
      }
    }
    return 'Visa';
  },

  /** check if at least one passenger has a medical condition */
  passengersHaveMobilityLimitations: (state, getters) => {
    return getters.passengersDetails.some(passenger => passenger.mobilityLimitations);
  },

  /** get payment data to send to API */
  paymentData: (state, getters, rootState, rootGetters) => {
    return {
      ...getters.reservationData,
      ...rootGetters.formDataFlat(state.payment)
    }
  },

  /** check if the payment form is valid */
  paymentFormIsValid: (state, getters, rootState, rootGetters) => {
    return (
      rootGetters.formIsValid(['checkout', 'payment'])
    );
  },

  /** checkout is reservation if cannot pay or mobility limitations */
  isReservation: (state, getters) => {
    return getters.passengersHaveMobilityLimitations || (state.payment && !state.payment.canPay);
  },

  /** get reservation data to send to API */
  reservationData: state => {
    return {
      quoteId: state.quote.quoteId,
      currentPageUrl: window.location.origin + window.location.pathname
    }
  }
}